import { useEffect, useState } from "react";
import { KeyRound, LockKeyhole, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";

export function QuickLock() {
  const { user } = useAuth();
  const key = `odonto.quickLock.${user?.id || "anonymous"}`;
  const [locked, setLocked] = useState(() => sessionStorage.getItem(key) === "1");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLocked(sessionStorage.getItem(key) === "1");
  }, [key]);

  const lock = () => {
    sessionStorage.setItem(key, "1");
    setPassword("");
    setError("");
    setLocked(true);
  };

  const unlock = async () => {
    if (!password) {
      setError("Digite sua senha para desbloquear.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/auth.php?action=verify_password", {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.ok) throw new Error(data?.error || "Senha incorreta");
      sessionStorage.removeItem(key);
      setPassword("");
      setLocked(false);
    } catch (unlockError: unknown) {
      setError(unlockError instanceof Error ? unlockError.message : "Não foi possível desbloquear.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button variant="ghost" size="icon" title="Bloquear tela" onClick={lock}>
        <LockKeyhole className="h-4 w-4" />
        <span className="sr-only">Bloquear tela</span>
      </Button>
      {locked && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-background/95 p-4 backdrop-blur-md">
          <div className="w-full max-w-sm rounded-md border bg-card p-6 shadow-xl">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <LockKeyhole className="h-6 w-6" />
            </div>
            <h2 className="text-center text-xl font-semibold">Tela bloqueada</h2>
            <p className="mt-1 text-center text-sm text-muted-foreground">
              {user?.email}. Sua sessão continua ativa e os dados permanecem protegidos.
            </p>
            <div className="mt-5 space-y-3">
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  autoFocus
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  onKeyDown={(event) => event.key === "Enter" && unlock()}
                  placeholder="Senha da conta"
                  className="pl-9"
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button className="w-full" onClick={unlock} disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Desbloquear
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
